import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Alert from "@mui/material/Alert";
import Skeleton from "@mui/material/Skeleton";
import LinearProgress from "@mui/material/LinearProgress";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTheme } from "@mui/material/styles";
import { LineChart } from "@mui/x-charts/LineChart";
import { useTranslation } from "react-i18next";
import {
  DASHBOARD_GRAPH_PERIODS,
  DEFAULT_DASHBOARD_GRAPH_PERIOD,
  useInvestmentGraphQuery,
} from "@query/dashboardGraphQuery";
import {
  formatDashboardDate,
  formatDashboardDateRange,
  resolveDashboardLocale,
} from "../utils/chartLocale";

const PERIOD_TRANSLATION_KEYS = {
  "1year": "main:dashboard.periods.oneYear",
  "6months": "main:dashboard.periods.sixMonths",
  "3months": "main:dashboard.periods.threeMonths",
  "1month": "main:dashboard.periods.oneMonth",
  "1week": "main:dashboard.periods.oneWeek",
};

function getTickStep(length) {
  if (length > 180) return 30;
  if (length > 90) return 14;
  if (length > 45) return 7;
  if (length > 21) return 3;
  return 1;
}

function formatCzk(value, formatter) {
  return `${formatter.format(value ?? 0)} K\u010d`;
}

function formatCzkCompact(value, formatter) {
  return `${formatter.format(value ?? 0)} K\u010d`;
}

function AreaGradient({ color, id }) {
  return (
    <defs>
      <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
        <stop offset="0%" stopColor={color} stopOpacity={0.45} />
        <stop offset="100%" stopColor={color} stopOpacity={0} />
      </linearGradient>
    </defs>
  );
}

export default function InvestmentTrendChart() {
  const theme = useTheme();
  const { t, i18n } = useTranslation(["main"]);
  const [period, setPeriod] = React.useState(DEFAULT_DASHBOARD_GRAPH_PERIOD);
  const chartHeight = 320;

  const investmentGraphQuery = useInvestmentGraphQuery(period);
  const payload = investmentGraphQuery.data?.data;
  const locale = resolveDashboardLocale(i18n.language);

  const moneyFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [locale]
  );

  const compactMoneyFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(locale, {
        notation: "compact",
        maximumFractionDigits: 1,
      }),
    [locale]
  );

  const chartPoints = React.useMemo(() => {
    if (!Array.isArray(payload?.daily)) {
      return [];
    }

    return payload.daily.map((point) => ({
      dateLabel: formatDashboardDate(point.date, locale, {
        month: "short",
        day: "numeric",
      }),
      amount: Number(point.amount) || 0,
    }));
  }, [payload?.daily, locale]);

  const totalAmount = React.useMemo(() => {
    if (typeof payload?.total_amount === "number") {
      return payload.total_amount;
    }

    return chartPoints.reduce((sum, point) => sum + point.amount, 0);
  }, [chartPoints, payload?.total_amount]);

  const rangeLabel = React.useMemo(
    () =>
      formatDashboardDateRange(
        payload?.from_date,
        payload?.to_date,
        locale,
        t("main:dashboard.periodRangeFallback")
      ),
    [payload?.from_date, payload?.to_date, locale, t]
  );

  const xAxisData = chartPoints.map((point) => point.dateLabel);
  const seriesData = chartPoints.map((point) => point.amount);
  const hasData = chartPoints.length > 0;
  const isLoading = investmentGraphQuery.isLoading && !payload;
  const gradientId = "investment-graph-gradient";

  return (
    <Card
      variant="outlined"
      sx={{
        width: "100%",
        borderRadius: 3,
        overflow: "hidden",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        background:
          "radial-gradient(circle at top right, rgba(25, 118, 210, 0.2) 0%, rgba(25, 118, 210, 0.06) 28%, rgba(25, 118, 210, 0) 62%)",
      }}
    >
      {investmentGraphQuery.isFetching && !isLoading ? (
        <LinearProgress />
      ) : (
        <Box sx={{ height: 4 }} />
      )}
      <CardContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", flex: 1 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          sx={{
            alignItems: { xs: "stretch", sm: "flex-start" },
            justifyContent: "space-between",
            mb: 2,
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: 0.5 }}>
              <TrendingUpRoundedIcon color="primary" fontSize="small" />
              <Typography component="h2" variant="subtitle2">
                {t("main:dashboard.investment.title")}
              </Typography>
            </Stack>
            <Typography variant="h4" component="p" sx={{ fontWeight: 700 }}>
              {formatCzk(totalAmount, moneyFormatter)}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {rangeLabel}
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: { xs: "100%", sm: 170 } }}>
            <InputLabel id="investment-period-label">
              {t("main:dashboard.period.label")}
            </InputLabel>
            <Select
              labelId="investment-period-label"
              id="investment-period"
              value={period}
              label={t("main:dashboard.period.label")}
              onChange={(event) => setPeriod(event.target.value)}
            >
              {DASHBOARD_GRAPH_PERIODS.map((periodValue) => (
                <MenuItem key={periodValue} value={periodValue}>
                  {t(PERIOD_TRANSLATION_KEYS[periodValue])}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            alignItems: "flex-start",
            mb: 2,
            px: 1.25,
            py: 1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <InfoOutlinedIcon sx={{ color: "text.secondary", fontSize: 18, mt: "2px" }} />
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {t("main:dashboard.investment.description")}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {t("main:dashboard.investment.calculation")}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ mt: "auto" }}>
          {isLoading ? <Skeleton variant="rounded" height={chartHeight} /> : null}

          {!isLoading && investmentGraphQuery.isError ? (
            <Alert severity="error">{t("main:dashboard.errors.loadInvestment")}</Alert>
          ) : null}

          {!isLoading && !investmentGraphQuery.isError && !hasData ? (
            <Box
              sx={{
                height: chartHeight,
                borderRadius: 2,
                border: "1px dashed",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                px: 2,
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("main:dashboard.noData")}
              </Typography>
            </Box>
          ) : null}

          {!isLoading && !investmentGraphQuery.isError && hasData ? (
            <LineChart
              colors={[theme.palette.primary.main]}
              xAxis={[
                {
                  scaleType: "point",
                  data: xAxisData,
                  tickInterval: (_, index) => index % getTickStep(xAxisData.length) === 0,
                },
              ]}
              yAxis={[
                {
                  valueFormatter: (value) => formatCzkCompact(value, compactMoneyFormatter),
                },
              ]}
              series={[
                {
                  id: "investment",
                  label: t("main:dashboard.investment.amount"),
                  data: seriesData,
                  showMark: seriesData.length <= 45,
                  area: true,
                  curve: "monotoneX",
                  valueFormatter: (value) => formatCzk(value, moneyFormatter),
                },
              ]}
              height={chartHeight}
              margin={{ left: 60, right: 20, top: 24, bottom: 36 }}
              grid={{ horizontal: true }}
              slotProps={{
                legend: {
                  hidden: true,
                },
              }}
              sx={{
                "& .MuiAreaElement-series-investment": {
                  fill: `url('#${gradientId}')`,
                },
              "& .MuiLineElement-series-investment": {
                strokeWidth: 3,
                filter: "drop-shadow(0px 6px 10px rgba(25, 118, 210, 0.24))",
              },
              "& .MuiMarkElement-series-investment": {
                fill: theme.palette.primary.main,
                stroke: theme.palette.primary.main,
                strokeWidth: 1,
              },
            }}
          >
              <AreaGradient color={theme.palette.primary.main} id={gradientId} />
            </LineChart>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}
